import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getAccountID } from '../../../../utils/auth'
import {
  addNewEntityConfigurationItemTemplate,
  deleteOrganicRankManagementKeywordsAction,
  dispose,
  EMPTY_KEYWORD,
  getOrganicRankManagementAction,
  getOrganicRankManagementKeywordAction,
  patchOrganicRankManagementKeywordsAction,
  selectEntityConfiguration,
  selectIsItemsUpdating,
  selectOrganicRanksAdditionalInfo,
  selectOrganicRanksKeysToDelete,
  selectOrganicRanksMaxKeywordNumberData,
  setAdditionalInfo,
} from '../../../../store/slice/organicRankModal'
import { errorParserRequest } from '../../../../utils/errorParser'
import { TOAST_DEFAULT_CONFIG } from '../../../../constants/toast'

export const useRankData = ({ productData, setFooterHeight }) => {
  const keywordList = useSelector(selectEntityConfiguration)
  const keysToDelete = useSelector(selectOrganicRanksKeysToDelete)
  const maxKeywordNumber = useSelector(selectOrganicRanksMaxKeywordNumberData)
  const isItemsUpdating = useSelector(selectIsItemsUpdating)
  const { channelID, productID, accountID } = useSelector(selectOrganicRanksAdditionalInfo)
  const dispatch = useDispatch()

  const footerRef = useRef(null)

  const isChangesExist = useMemo(
    () =>
      keysToDelete.length > 0 ||
      keywordList.find(
        (item) =>
          item.currentBidFactorInit !== item.currentBidFactor ||
          item.currentFixedBid.fixedBid_exact !== item.currentFixedBid.fixedBid_exact_init ||
          item.currentFixedBid.fixedBid_phrase !== item.currentFixedBid.fixedBid_phrase_init ||
          item.searchPhrase !== item.searchPhraseInit
      ),
    [keywordList, keysToDelete]
  )

  const isPossibleToAddNewKeyword = useMemo(() => {
    if (keywordList.length < maxKeywordNumber) {
      return !keywordList.find((item) => !item.searchPhrase)
    }
    return false
  }, [keywordList, maxKeywordNumber])

  const addNewKeywordTemplate = useCallback(() => {
    dispatch(
      addNewEntityConfigurationItemTemplate({
        id: keywordList.length,
        ...EMPTY_KEYWORD,
      })
    )
  }, [keywordList])

  const saveChanges = useCallback(() => {
    const listToSubmit = keywordList.map((item) => {
      if (item.type === 'keyword_temp') {
        return {
          searchPhrase: item.searchPhrase,
          currentBidFactor: item.currentBidFactor || 0,
        }
      }

      const isChanged =
        item.currentBidFactorInit !== item.currentBidFactor ||
        item.currentFixedBid.fixedBid_exact !== item.currentFixedBid.fixedBid_exact_init ||
        item.currentFixedBid.fixedBid_phrase !== item.currentFixedBid.fixedBid_phrase_init ||
        item.searchPhrase !== item.searchPhraseInit

      if (!isChanged) return null

      return {
        currentBidFactor: item.currentBidFactor || 0,
        currentFixedBid: {
          fixedBid_exact: Number(item.currentFixedBid.fixedBid_exact) || null,
          fixedBid_phrase: Number(item.currentFixedBid.fixedBid_phrase) || null,
        },
        searchPhrase: item.searchPhrase,
      }
    })

    if (keysToDelete.length) {
      dispatch(
        deleteOrganicRankManagementKeywordsAction({
          channelID,
          productID,
          accountID,
          data: keysToDelete,
        })
      )
        .unwrap()
        .then(() => {
          toast.success(`Keyword deleted successfully.`, TOAST_DEFAULT_CONFIG)
        })
        .catch(() => {
          //   empty
        })
    }

    dispatch(
      patchOrganicRankManagementKeywordsAction({
        channelID,
        productID,
        accountID,
        data: listToSubmit.filter((item) => item),
      })
    )
      .unwrap()
      .then(() => {
        dispatch(
          getOrganicRankManagementKeywordAction({
            accountID,
            channelID,
            productID,
          })
        )
      })
      .catch((err) => {
        toast.error(errorParserRequest(err), TOAST_DEFAULT_CONFIG)
      })
  }, [channelID, productID, accountID, keywordList, keysToDelete])

  useEffect(() => {
    if (channelID && productID && accountID) {
      dispatch(
        getOrganicRankManagementAction({
          accountID,
          channelID,
          productID,
        })
      )
    }
  }, [channelID, productID, accountID])

  useEffect(() => {
    dispatch(
      setAdditionalInfo({
        accountID: getAccountID(),
        channelID: productData.channelID,
        productID: productData.productID,
        isBol: true,
      })
    )
  }, [productData])

  useEffect(() => {
    if (!footerRef.current) return

    setFooterHeight(footerRef.current.scrollHeight)
  }, [])

  useEffect(
    () => () => {
      dispatch(dispose())
    },
    []
  )

  return {
    keywordList,
    isChangesExist,
    saveChanges,
    addNewKeywordTemplate,
    footerRef,
    accountID,
    channelID,
    productID,
    isPossibleToAddNewKeyword,
    isItemsUpdating,
  }
}
